import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts';
import { usePasswordDisplay } from '@/hooks';
import { validatePassword } from '@/utils/validatePassword';
import { Button } from '../ui';
import BaseInput from '../ui/BaseInput';
import MaskPasswordButton from '../ui/shared/MaskPasswordButton';
import { ThemeToggle, LanguageSwitcher } from '@/components/ui';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { buildApiUrl } from '@/constants';

interface ResetPasswordFormData {
  email: string;
  token: string;
  newPassword: string;
  confirmPassword: string;
}

const ResetPasswordPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showPassword, setShowPassword } = usePasswordDisplay();
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState<ResetPasswordFormData>({
    email: '',
    token: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | undefined>(undefined);

  // Extract token and email from URL params, or fetch email from token
  useEffect(() => {
    const token = searchParams.get('token');
    const email = searchParams.get('email');
    
    if (token) {
      setFormData(prev => ({ ...prev, token }));
      
      // If no email in URL, fetch it from the token
      if (!email) {
        fetchEmailFromToken(token);
      }
    }
    
    if (email) {
      setFormData(prev => ({ ...prev, email }));
    }
  }, [searchParams]);

  const fetchEmailFromToken = async (token: string) => {
    try {
      const url = buildApiUrl(`/api/auth/reset-password/email/${token}`);
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, email: data.email }));
      } else {
        console.error('Failed to fetch email from token:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching email from token:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setConfirmPasswordError(t('passwordsDontMatch'));
      return;
    }
    
    // Validate password strength
    const passwordValidationErrors = validatePassword(formData.newPassword);
    if (passwordValidationErrors.length > 0) {
      setPasswordErrors(passwordValidationErrors);
      return;
    }

    setLoading(true);
    setError(null);
    setPasswordErrors([]);
    setConfirmPasswordError(undefined);

    try {
      const url = buildApiUrl('/api/auth/reset-password/confirm');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          token: formData.token,
          newPassword: formData.newPassword,
        }),
      });

      if (response.ok) {
        setSuccess(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('unexpectedError'));
      }
    } catch (err) {
      setError(t('networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof ResetPasswordFormData) => (value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    if (field === 'newPassword') {
      const errs = validatePassword(value);
      setPasswordErrors(errs);
      
      // Clear confirm password error when new password changes
      if (confirmPasswordError) {
        setConfirmPasswordError(undefined);
      }
    }
    
    if (field === 'confirmPassword') {
      // Clear confirm password error when it changes
      if (confirmPasswordError) {
        setConfirmPasswordError(undefined);
      }
    }
  };

  // Auto-redirect to login after successful password reset
  useEffect(() => {
    if (success) {
      const redirectTimer = setTimeout(() => {
        navigate('/auth/login', { 
          state: { 
            message: 'Password reset successfully! Please sign in with your new password.' 
          }
        });
      }, 3000); // Redirect after 3 seconds

      return () => clearTimeout(redirectTimer);
    }
  }, [success, navigate]);

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950 transition-colors duration-300 flex flex-col">
        {/* Top Bar */}
        <div className="w-full bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800 transition-colors duration-300">
          <div className="container mx-auto px-4 py-3 flex justify-end gap-2 sm:gap-3">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white dark:bg-slate-800 py-8 px-4 shadow dark:shadow-lg dark:shadow-slate-900/50 sm:rounded-lg sm:px-10 border border-gray-200 dark:border-slate-700 transition-colors duration-300">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30">
                  <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-slate-100 transition-colors duration-300">{t('passwordResetSuccessfully')}</h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-slate-300 transition-colors duration-300">
                  {t('passwordUpdated')}
                </p>
                <p className="mt-2 text-xs text-gray-500 dark:text-slate-400 transition-colors duration-300">
                  Redirecting to login in 3 seconds...
                </p>
                <div className="mt-6">
                  <Button
                    onClick={() => navigate('/auth/login')}
                    className="w-full bg-brand-dark-blue hover:bg-brand-electric-blue text-white"
                  >
                    {t('signIn')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 transition-colors duration-300 flex flex-col">
      {/* Top Bar */}
      <div className="w-full bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800 transition-colors duration-300">
        <div className="container mx-auto px-4 py-3 flex justify-end gap-2 sm:gap-3">
          <LanguageSwitcher />
          <ThemeToggle />
        </div>
      </div>

      <div className="flex-1 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white dark:bg-slate-800 py-8 px-4 shadow dark:shadow-lg dark:shadow-slate-900/50 sm:rounded-lg sm:px-10 border border-gray-200 dark:border-slate-700 transition-colors duration-300">
            <div className="mb-6">
              <button
                onClick={() => navigate('/auth/login')}
                className="flex items-center text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-100 mb-4 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-300"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                {t('backToLogin')}
              </button>
              <h2 className="text-center text-3xl font-extrabold text-gray-900 dark:text-slate-100 transition-colors duration-300">
                {t('resetPassword')}
              </h2>
              <p className="mt-2 text-center text-sm text-gray-600 dark:text-slate-300 transition-colors duration-300">
                {t('enterYourEmailAndNewPasswordToCompleteTheReset')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm text-center">
                  {error}
                </div>
              )}

              <BaseInput
                label={t('email')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                required
                autoComplete="email"
                placeholder={t('enterYourEmailAddress')}
                readOnly={true}
                className="bg-gray-50 cursor-not-allowed"
              />

              <BaseInput
                label={t('enterYourNewPassword')}
                name="newPassword"
                type={showPassword ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={handleChange('newPassword')}
                error={
                  passwordErrors.length > 0
                    ? passwordErrors.map((err) => t(err)).join(', ')
                    : undefined
                }
                required
                autoComplete="new-password"
                placeholder={t('enterYourNewPassword')}
                rightAddon={
                  <MaskPasswordButton 
                    showPassword={showPassword} 
                    setShowPassword={setShowPassword} 
                  />
                }
              />

              <BaseInput
                label={t('confirmYourNewPassword')}
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange('confirmPassword')}
                error={confirmPasswordError}
                required
                autoComplete="new-password"
                placeholder={t('confirmYourNewPassword')}
                rightAddon={
                  <MaskPasswordButton 
                    showPassword={showConfirmPassword} 
                    setShowPassword={setShowConfirmPassword} 
                  />
                }
              />

              <Button
                type="submit"
                loading={loading}
                className="w-full py-3 bg-brand-dark-blue hover:bg-brand-electric-blue text-white font-medium rounded-md"
              >
                {t('resetPassword')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600 dark:text-slate-300 transition-colors duration-300">
                {t('rememberYourPassword')}{' '}
                <button
                  onClick={() => navigate('/auth/login')}
                  className="text-brand-electric-blue dark:text-blue-400 hover:text-brand-dark-blue dark:hover:text-blue-300 font-medium transition-colors duration-300"
                >
                  {t('signIn')}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
